import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { useInspect } from '@/designs-dev/inspect/InspectContext';
import { baseName } from '@/designs-dev/inspect/fiber';
import { writeClipboard } from '@/designs-dev/loft/command';
import CopyForClaude from '@/designs-dev/loft/CopyForClaude';
import CodeView from '@/designs-dev/inspect/CodeView';
import CommentsPanel from '@/designs-dev/comments/CommentsPanel';
import { resolveAnchors } from '@/designs-dev/comments/anchor';
import { useComments } from '@/designs-dev/comments/store';
import { REGISTRY } from '@/designs-dev/inspect/ds-registry';
import {
  meaningfulTweaks,
  resetAllTweaks,
  resetTweak,
  tweakCommand,
  useTweaks,
} from '@/designs-dev/inspect/tweaks';
import {
  CHROME_VH,
  CHROME_VW,
  CHROME_ZOOM,
} from '@/designs-dev/inspect/chrome';

// The session's tweaks, from the canvas handles. Above the code because it is
// the thing being worked on; gone when there is nothing in it.
const TweakBar = () => {
  const tweaks = useTweaks();
  // The count on the button is what the clipboard gets, which leaves out no-ops.
  const sending = meaningfulTweaks(tweaks).length;
  if (tweaks.length === 0) return null;
  return (
    <Stack spacing={1} sx={{ mb: 2, flexShrink: 0 }}>
      {tweaks.map((t) => (
        <Stack
          key={t.id}
          direction='row'
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography variant='mono' sx={{ minWidth: 0 }} noWrap>
            {baseName(t.component)} · {t.prop} {t.from} → {t.to}
          </Typography>
          <IconButton
            aria-label={`Reset ${t.prop}`}
            size='small'
            onClick={() => resetTweak(t.id)}
          >
            <Icon icon={'xmark' as never} fontSize={12} />
          </IconButton>
        </Stack>
      ))}
      <Stack direction='row' spacing={2}>
        <Button
          size='small'
          variant='contained'
          disabled={sending === 0}
          onClick={() => writeClipboard(tweakCommand(tweaks))}
        >
          Copy {sending} for Claude
        </Button>
        <Button size='small' variant='outlined' onClick={resetAllTweaks}>
          Reset all
        </Button>
      </Stack>
    </Stack>
  );
};

// A hairline that stretches to the drawer's edges. It MUST be a plain block
// child (hence the wrapping Box) — a negative margin on a flex item doesn't
// reliably stretch past the parent, so a bare <Divider> in a Stack stops at the
// padded edge. Wrapped like this the mx:-3 cancels the panel's p={3}, and the
// panel's overflowX:clip trims it flush to the edge.
const FullBleedDivider = ({ mb }: { mb?: number }) => (
  <Box sx={{ mb: mb }}>
    <Divider sx={{ mx: -3 }} />
  </Box>
);

// What the copy button beside the name hands over: the URL (which encodes drill
// mode, scene and the pinned component), the component and its source file, so
// the view never has to be described in prose. Shared with the flows pages'
// minis; only the pointer is this panel's.
const componentPointer = (name: string, file?: string): string =>
  [
    window.location.pathname + window.location.search,
    `Component: ${name}`,
    `Source: ${file ?? 'raw MUI (themed via llama) — no prototype file'}`,
  ].join('\n');

// Sits beside the phone in drill mode and shows the selected component's code:
// as it is used here, then the file it is defined in. It used to carry the
// component's notes instead (its grade, props, registry card, theme tweaks);
// the code turned out to be what was being looked for.
const InspectPanel = () => {
  const { active, mode, current, setCurrent, screenEl } = useInspect();
  // In comment mode the dock holds the list of threads, once it's asked for.
  const { listOpen } = useComments();
  const commenting = mode === 'comment' && listOpen;
  if (!active && !commenting) return null;

  // A raw MUI component sharing a name with one of ours (MUI's Card and the
  // wrapped Card) is MUI, so it does not borrow our component's file.
  const entry =
    current && !current.muiBase ? REGISTRY[baseName(current.name)] : undefined;
  const file = current?.file ?? entry?.file;

  return (
    // Docked on the right at a width that leaves the phone room: a fixed 640
    // took half a laptop's screen. On a phone there is no right to dock on, so
    // it becomes a sheet over the lower half of the screen instead.
    <Box
      sx={{
        // Full width on a phone, divided back out of the zoom.
        width: { xs: CHROME_VW(100), md: 400, lg: 480, xl: 640 },
        flexShrink: 0,
        zoom: CHROME_ZOOM,
        height: { xs: CHROME_VH(50), md: CHROME_VH(100) },
        bgcolor: 'background.paper',
        borderLeft: { xs: 0, md: 1 },
        borderTop: { xs: 1, md: 0 },
        borderColor: 'custom.neutral.200',
        p: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        position: { xs: 'fixed', md: 'sticky' },
        top: { xs: 'auto', md: 0 },
        bottom: { xs: 0, md: 'auto' },
        left: { xs: 0, md: 'auto' },
        zIndex: { xs: 1150, md: 'auto' },
        overflowY: 'auto',
        overflowX: 'clip',
      }}
    >
      {commenting ? (
        <CommentsPanel
          placedOf={(anchors) =>
            new Set(screenEl ? resolveAnchors(screenEl, anchors).keys() : [])
          }
        />
      ) : (
        <>
          <Stack
            sx={{ alignItems: 'center', mb: 2, flexShrink: 0 }}
            direction='row'
            spacing={1}
          >
            <Typography variant='h3' sx={{ flex: 1, minWidth: 0 }} noWrap>
              {current ? baseName(current.name) : 'Code'}
            </Typography>
            {current && (
              <>
                <CopyForClaude
                  text={() => componentPointer(baseName(current.name), file)}
                />
                <Chip
                  size='small'
                  label='selected'
                  color='cherry'
                  onDelete={() => setCurrent(null)}
                />
              </>
            )}
          </Stack>

          <FullBleedDivider mb={2} />

          <TweakBar />

          {current ? (
            <CodeView current={current} file={file} />
          ) : (
            // Just the mark: pointing at something fills the panel, which teaches
            // it faster than instructions did.
            <Box sx={{ color: 'text.secondary' }}>
              <Icon icon={'magnifying-glass' as never} fontSize={24} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default InspectPanel;
